import { useState, useEffect } from "react";
import { Button, Checkbox, Dropdown } from "@optimizely/axiom";
import { APPLY, GROUP_TYPES, GROUP_TYPES_FILTER_IDS, GROUP_TYPES_SAVE_FILTERS } from "../../../constants/constants";
import { capitalizeText } from "../../../lib/utils";
import { useAnalyticsTracking } from "../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import {
    ANALYTICS_EVENT_NAMES,
    ANALYTICS_TRACKED_COMPONENTS,
    ANALYTICS_FLOWS
} from "../../../constants/analytics-constants";
import { Flags } from "../../../feature-flags/flags";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";

type UserGroupFilterDropdownProps = {
    appliedGroupTypes: string[]; // Array of selected group types
    onChange: (groupTypes: string[]) => void; // Function to apply the selected group types
    "data-cy"?: string;
};

export const UserGroupMultiFilterDropdown = ({
    appliedGroupTypes,
    onChange,
    "data-cy": dataCy
}: UserGroupFilterDropdownProps) => {
    const [saveSelection, setSaveSelection] = useState(false);
    const { enabled: enableSaveGroupFilterSelection } = useFeatureFlag(Flags.ENABLE_SAVE_GROUP_FILTER_SELECTION);
    const [selectedGroupTypes, setSelectedGroupTypes] = useState<string[]>(appliedGroupTypes || []);
    const { sendTrackEvent } = useAnalyticsTracking();

    useEffect(() => {
        const saveEnabled = localStorage.getItem(GROUP_TYPES_SAVE_FILTERS);
        if (saveEnabled === "true") {
            setSaveSelection(true);
        }
    }, []);

    // Sync internal state when appliedGroupTypes prop changes
    useEffect(() => {
        setSelectedGroupTypes(appliedGroupTypes || []);
    }, [appliedGroupTypes]);

    const allowedFilter = [GROUP_TYPES.CUSTOM, GROUP_TYPES.INTERNAL, GROUP_TYPES.PRODUCT];
    const groupTypes = allowedFilter.map((groupType) => capitalizeText(groupType));

    const handleSelectGroupType = (groupType: string) => {
        if (selectedGroupTypes?.includes(groupType)) {
            setSelectedGroupTypes((currentGroupTypes) => currentGroupTypes.filter((type) => type !== groupType));
        } else {
            setSelectedGroupTypes((currentGroupTypes) => [...currentGroupTypes, groupType]);
        }
    };
    const handleApplyGroupTypes = (func: () => void) => {
        if (saveSelection) {
            localStorage.setItem(GROUP_TYPES_FILTER_IDS, selectedGroupTypes.join(","));
            localStorage.setItem(GROUP_TYPES_SAVE_FILTERS, "true");
        } else {
            localStorage.removeItem(GROUP_TYPES_FILTER_IDS);
            localStorage.removeItem(GROUP_TYPES_SAVE_FILTERS);
        }
        onChange(selectedGroupTypes);
        func(); // Call the function to hide the dropdown contents
        sendTrackEvent({
            name: ANALYTICS_EVENT_NAMES.UPDATE_GROUP_TYPE_FILTERS,
            component: ANALYTICS_TRACKED_COMPONENTS.GROUPS_TAB,
            flow: ANALYTICS_FLOWS.GROUP_ACCESS,
            data: {
                appliedFilters: selectedGroupTypes.sort().join(", ")
            }
        });
    };
    const appliedGroupTypesString = [...appliedGroupTypes].sort().join(", ");

    return (
        /* eslint-disable react/style-prop-object */
        <Dropdown
            data-cy={dataCy}
            buttonContent={{
                label: "Filter by Group Type",
                content: appliedGroupTypesString
            }}
            style="plain"
            arrowIcon="down"
            width={350}
            shouldHideChildrenOnClick={false}
        >
            {({ handleHideChildren }: { handleHideChildren: () => void }) => (
                <Dropdown.Contents>
                    {groupTypes.map((groupType) => {
                        return (
                            <Dropdown.ListItem role="option" key={groupType}>
                                <Dropdown.BlockLink
                                    isItemSelected={selectedGroupTypes?.includes(groupType)}
                                    onClick={() => handleSelectGroupType(groupType)}
                                    isMultiSelect={true}
                                >
                                    {groupType}
                                </Dropdown.BlockLink>
                            </Dropdown.ListItem>
                        );
                    })}
                    <Dropdown.ListItem key={APPLY} role="separator" ignoreToggle={true}>
                        <div className={"flex flex-justified--between push--sides push--ends"}>
                            <Button
                                style="highlight"
                                size={"small"}
                                isDisabled={selectedGroupTypes.length < 1}
                                onClick={() => handleApplyGroupTypes(handleHideChildren)}
                            >
                                {APPLY}
                            </Button>
                            {enableSaveGroupFilterSelection && (
                                <Checkbox
                                    label="Save Selection"
                                    checked={saveSelection}
                                    onChange={() => setSaveSelection(!saveSelection)}
                                />
                            )}
                        </div>
                    </Dropdown.ListItem>
                </Dropdown.Contents>
            )}
        </Dropdown>
    );
};

UserGroupMultiFilterDropdown.displayName = "UserGroupMultiFilterDropdown";
